import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Observable, Subscription } from 'rxjs';
import { AiChatService, ChatMessage } from 'src/app/services/ai-chat.service';
import { AiService } from 'src/app/services/ai.service';
import { ProtocolosService } from 'src/app/services/protocolos.service';
import { SolicitacoesService } from 'src/app/services/solicitacoes.service';

@Component({
  selector: 'app-ai-chat-floating',
  templateUrl: './ai-chat-floating.component.html',
  styleUrls: ['./ai-chat-floating.component.scss']
})
export class AiChatFloatingComponent implements OnInit, OnDestroy {
  @HostBinding('style.display') hostDisplay = 'none';

  protected useFloatingVisibility = true;

  faTrash = faTrash;
  newMessage = '';
  messages: ChatMessage[] = [];
  isChatOpen = false;
  protected subscriptions = new Subscription();
  
  suggestedActions = [
    'Verificar conformidade do fornecedor X',
    'Resumo dos protocolos pendentes',
    'Analisar novo arquivo de fornecedor'
  ];

  constructor(
    protected aiService: AiService,
    protected router: Router,
    protected snackBar: MatSnackBar,
    protected protocolosService: ProtocolosService,
    protected solicitacoesService: SolicitacoesService,
    protected chatService: AiChatService
  ) {}

  ngOnInit(): void {
    if (this.useFloatingVisibility) {
      this.subscriptions.add(
        this.chatService.isChatOpen$.subscribe((isOpen) => {
          this.isChatOpen = isOpen;
          this.hostDisplay = isOpen ? 'block' : 'none';
        })
      );
    } else {
      this.isChatOpen = true;
      this.hostDisplay = 'block';
    }

    this.subscriptions.add(
      this.chatService.messages$.subscribe((msgs) => {
        this.messages = msgs;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openChat(): void {
    this.chatService.toggleChat(true);
  }

  closeChat(): void {
    this.chatService.toggleChat(false);
  }

  clearChat(): void {
    this.chatService.clearChat();
  }

  sendMessage(): void {
    const userMessage = this.newMessage.trim();
    if (!userMessage) {
      return;
    }

    const userMsg: ChatMessage = {
      sender: 'user',
      text: userMessage,
      time: this.getCurrentTime()
    };
    this.chatService.addMessage(userMsg);
    this.newMessage = '';

    const loadingMessage: ChatMessage = {
      sender: 'ai',
      text: '...',
      time: this.getCurrentTime()
    };
    this.chatService.addMessage(loadingMessage);
    const currentMessages = this.chatService.getMessages();

    const historyForAi = currentMessages
      .filter((message) => message !== loadingMessage)
      .map((message) => ({ sender: message.sender, text: message.text }));

    this.aiService.sendMessageToAi(userMessage, historyForAi).subscribe({
      next: (response) => {
        const updatedMessages = currentMessages.filter((message) => message !== loadingMessage);
        const aiResponse = response;
        let newAiMessage: ChatMessage | null = null;

        if (typeof aiResponse === 'string') {
          newAiMessage = {
            sender: 'ai',
            text: aiResponse,
            time: this.getCurrentTime()
          };
        } else if (aiResponse?.responseType === 'text') {
          newAiMessage = {
            sender: 'ai',
            text: aiResponse.text,
            time: this.getCurrentTime()
          };

          if (aiResponse.text?.includes('redirecioná-lo para a tela de Protocolos')) {
            newAiMessage.actionButton = {
              label: 'Ir para Protocolos',
              route: '/safe-gis/protocolos'
            };
          }
        } else if (aiResponse?.responseType === 'action' && aiResponse.action) {
          this.handleAiAction(aiResponse.text, aiResponse.action, updatedMessages);
          return;
        } else {
          newAiMessage = {
            sender: 'ai',
            text: aiResponse?.text || 'Desculpe, não consegui processar sua solicitação.',
            time: this.getCurrentTime()
          };
        }

        if (newAiMessage) {
          this.chatService.setMessages([...updatedMessages, newAiMessage]);
        }
      },
      error: (error) => {
        const updatedMessages = currentMessages.filter((message) => message !== loadingMessage);
        console.error('Erro ao comunicar com o assistente IA:', error);
        const errorMsg: ChatMessage = {
          sender: 'ai',
          text: 'Desculpe, houve um erro de comunicação com o assistente de IA. Tente novamente mais tarde.',
          time: this.getCurrentTime()
        };
        this.chatService.setMessages([...updatedMessages, errorMsg]);
        this.snackBar.open('Erro de comunicação com o Assistente IA.', 'Fechar', {
          duration: 5000,
          panelClass: ['alert_error']
        });
      }
    });
  }

  handleSuggestedAction(action: string): void {
    this.newMessage = action;
    this.sendMessage();
  }

  handleMessageAction(route: string): void {
    this.router.navigate([route]);
    this.snackBar.open(`Redirecionando para: ${route}`, 'OK', {
      duration: 3000,
      panelClass: ['alert_success']
    });
    this.closeChat();
  }

  private handleAiAction(responseText: string, action: any, currentMessages: ChatMessage[]): void {
    const aiTextMsg: ChatMessage = {
      sender: 'ai',
      text: responseText,
      time: this.getCurrentTime()
    };
    this.chatService.setMessages([...currentMessages, aiTextMsg]);

    const details = action?.details || {};

    if (action?.type === 'navigate' && details.route) {
      this.router.navigate([details.route]);
      this.snackBar.open(`Redirecionando para: ${details.route}`, 'OK', {
        duration: 3000,
        panelClass: ['alert_success']
      });
      this.closeChat();
      return;
    }

    if (action?.type !== 'api_call' || !details.endpoint) {
      this.chatService.addMessage({
        sender: 'ai',
        text: 'A ação solicitada pela IA não pôde ser executada por falta de detalhes.',
        time: this.getCurrentTime()
      });
      return;
    }

    const loadingMessage: ChatMessage = {
      sender: 'ai',
      text: 'Buscando dados...',
      time: this.getCurrentTime()
    };
    this.chatService.addMessage(loadingMessage);

    const endpoint = String(details.endpoint || '');
    const method = String(details.method || 'GET').toUpperCase();
    const payload = details.payload || {};

    let apiCall: Observable<any> | null = null;
    let successMessage = '';
    let responseFormatter: ((result: any) => string) | null = null;

    if (method === 'GET' && endpoint.includes('/protocol-service/list/filtered')) {
      apiCall = this.protocolosService.getProtocols(payload, !!payload.onlyActive);
      successMessage = 'Protocolos listados com sucesso.';
      responseFormatter = (result) => this.formatProtocolsResponse(result);
    } else if (
      method === 'GET' &&
      endpoint.includes('/geo-monitoring') &&
      endpoint.includes('/with-response/filtered')
    ) {
      apiCall = this.solicitacoesService.getListRequests(payload);
      successMessage = 'Solicitações listadas com sucesso.';
      responseFormatter = (result) => this.formatRequestsResponse(result);
    } else if (method === 'POST' && endpoint.includes('/geo-monitoring/register/with-protocol')) {
      const sendMail = payload.sendMail !== undefined ? payload.sendMail : true;
      const hasKML = payload.hasKML !== undefined ? payload.hasKML : false;
      const hasCoordinates = payload.hasCoordinates !== undefined ? payload.hasCoordinates : false;
      apiCall = this.solicitacoesService.registerRequest(payload, sendMail, hasKML, hasCoordinates);
      successMessage = 'Solicitação registrada com sucesso.';
      responseFormatter = (result) => `${successMessage}\n\n${JSON.stringify(result, null, 2)}`;
    }

    if (!apiCall) {
      const finalMessages = this.chatService.getMessages().filter((message) => message !== loadingMessage);
      this.chatService.setMessages([
        ...finalMessages,
        {
          sender: 'ai',
          text: `Ação de API não reconhecida: ${method} ${endpoint}`,
          time: this.getCurrentTime()
        }
      ]);
      this.snackBar.open('Ação de API não reconhecida.', 'Fechar', {
        duration: 5000,
        panelClass: ['alert_warning']
      });
      return;
    }

    apiCall.subscribe({
      next: (result) => {
        const finalMessages = this.chatService.getMessages().filter((message) => message !== loadingMessage);
        const resultText = responseFormatter ? responseFormatter(result) : JSON.stringify(result, null, 2);

        this.chatService.setMessages([
          ...finalMessages,
          {
            sender: 'ai',
            text: resultText,
            time: this.getCurrentTime()
          }
        ]);

        this.snackBar.open(successMessage, 'OK', {
          duration: 3000,
          panelClass: ['alert_success']
        });
      },
      error: (error) => {
        const finalMessages = this.chatService.getMessages().filter((message) => message !== loadingMessage);
        console.error('Erro ao executar ação de API do chat:', error);
        this.chatService.setMessages([
          ...finalMessages,
          {
            sender: 'ai',
            text: `Erro ao executar a ação de API: ${error?.message || 'Erro desconhecido'}`,
            time: this.getCurrentTime()
          }
        ]);
        this.snackBar.open('Erro ao executar a ação de API.', 'Fechar', {
          duration: 5000,
          panelClass: ['alert_error']
        });
      }
    });
  }

  private formatProtocolsResponse(result: any): string {
    const items = result?.content || [];
    if (!Array.isArray(items) || items.length === 0) {
      return 'Nenhum protocolo encontrado.';
    }

    let formattedText = 'Protocolos encontrados:\n\n';

    items.forEach((protocol: any) => {
      formattedText += `• ${protocol?.name || 'Sem nome'}\n`;

      const criterias = protocol?.criteria || [];
      if (Array.isArray(criterias) && criterias.length > 0) {
        formattedText += '  Critérios:\n';
        criterias.forEach((criteria: any) => {
          if (!criteria?.name) {
            return;
          }

          const criteriaName = this.protocolosService.translateCriteria(criteria.name);
          const params = (criteria.criteriaParams || [])
            .filter((param: any) => param?.value !== undefined && param?.value !== null)
            .map((param: any) => `${this.protocolosService.translateCriteria(param.name)}: ${param.value}`)
            .join(', ');

          const period =
            criteria?.type === 'GIS' && criteria?.period?.quantity && criteria?.period?.quantity !== '0'
              ? ` | Período: ${criteria.period.quantity} ${criteria.period.type === 'DAYS' ? 'dias' : criteria.period.type}`
              : '';

          formattedText += `    - ${criteriaName}${params ? ` (${params})` : ''}${period}\n`;
        });
      }

      formattedText += '\n';
    });

    return formattedText.trim();
  }

  private formatRequestsResponse(result: any): string {
    const items = result?.content || result?.data?.content || result?.data || [];
    if (!Array.isArray(items) || items.length === 0) {
      return 'Nenhuma solicitação encontrada.';
    }

    const lines = items.slice(0, 10).map((item: any) => {
      const producer = item?.producerName || item?.producer?.corporateName || item?.corporateName || 'Sem produtor';
      const requestNumber = item?.requestNumber || item?.request?.requestNumber || 'Sem número';
      const status = item?.status || item?.requestStatus || 'Sem status';
      return `• ${requestNumber} — ${producer} — ${status}`;
    });

    const total = result?.totalElements || result?.data?.totalElements || items.length;
    return `Solicitações encontradas (${total}):\n\n${lines.join('\n')}`;
  }

  private getCurrentTime(): string {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
